import { getMessages } from "@/i18n/runtime";

export interface UpgradeTooltipOptions {
  readonly id: string;
  readonly kicker?: string;
  readonly title: string;
  readonly region: string;
  readonly summary: string;
  readonly effectLabel?: string;
  readonly afterLabel: string;
  readonly costLabel?: string;
  readonly stateLabel?: string;
}

export interface UpgradeTooltipElements {
  readonly root: HTMLDivElement;
  readonly title: HTMLDivElement;
  readonly region: HTMLSpanElement;
  readonly summary: HTMLParagraphElement;
  readonly effectRow: HTMLDivElement;
  readonly effectLabel: HTMLSpanElement;
  readonly effectValue: HTMLSpanElement;
  readonly afterRow: HTMLDivElement;
  readonly afterLabel: HTMLSpanElement;
  readonly afterValue: HTMLSpanElement;
  readonly costRow: HTMLDivElement;
  readonly costLabel: HTMLSpanElement;
  readonly costValue: HTMLSpanElement;
  readonly stateRow: HTMLDivElement;
  readonly stateLabel: HTMLSpanElement;
  readonly stateValue: HTMLSpanElement;
}

export function createUpgradeTooltip(
  options: UpgradeTooltipOptions,
): UpgradeTooltipElements {
  const messages = getMessages();
  const root = document.createElement("div");
  root.id = options.id;
  root.className = "hover-idle-tooltip hover-idle-upgrade-tooltip";
  root.setAttribute("role", "tooltip");
  root.setAttribute("aria-hidden", "true");
  root.hidden = true;

  const header = document.createElement("div");
  header.className = "hover-idle-tooltip-header";

  const titleGroup = document.createElement("div");
  titleGroup.className = "hover-idle-tooltip-title-group";

  const kicker = document.createElement("div");
  kicker.className = "hover-idle-tooltip-kicker";
  kicker.textContent = options.kicker ?? messages.upgradeTooltip.kicker;

  const title = document.createElement("div");
  title.className = "hover-idle-tooltip-title";
  title.textContent = options.title;

  titleGroup.append(kicker, title);

  const region = document.createElement("span");
  region.className = "hover-idle-tooltip-region";
  region.textContent = options.region;

  header.append(titleGroup, region);

  const summary = document.createElement("p");
  summary.className = "hover-idle-tooltip-summary";
  summary.textContent = options.summary;

  const rows = document.createElement("div");
  rows.className = "hover-idle-tooltip-rows";

  const effect = createTooltipRow(
    options.effectLabel ?? messages.upgradeTooltip.effect,
  );
  const after = createTooltipRow(options.afterLabel);
  const cost = createTooltipRow(
    options.costLabel ?? messages.upgradeTooltip.cost,
  );
  const state = createTooltipRow(
    options.stateLabel ?? messages.upgradeTooltip.state,
  );
  state.row.classList.add("hover-idle-tooltip-state-row");

  rows.append(effect.row, after.row, cost.row, state.row);
  root.append(header, summary, rows);

  return {
    root,
    title,
    region,
    summary,
    effectRow: effect.row,
    effectLabel: effect.label,
    effectValue: effect.value,
    afterRow: after.row,
    afterLabel: after.label,
    afterValue: after.value,
    costRow: cost.row,
    costLabel: cost.label,
    costValue: cost.value,
    stateRow: state.row,
    stateLabel: state.label,
    stateValue: state.value,
  };
}

function createTooltipRow(labelText: string): {
  readonly row: HTMLDivElement;
  readonly label: HTMLSpanElement;
  readonly value: HTMLSpanElement;
} {
  const row = document.createElement("div");
  row.className = "hover-idle-tooltip-row";

  const label = document.createElement("span");
  label.className = "hover-idle-tooltip-label";
  label.textContent = labelText;

  const value = document.createElement("span");
  value.className = "hover-idle-tooltip-value";

  row.append(label, value);
  return { row, label, value };
}
